import { SalesPageState } from 'client/courses/components/page-builder/page-editor';
import { CalendarPageEditor } from './calendar-page-editor';
import { blockDefinitions } from './block-definitions';
import { rpx } from 'client/lib/rpx-client';
import { type RouteLoadProps, type LoadedProps } from '@components/router';
import { type PageTheme } from './theme-panel';
import { type SalespageContent } from 'server/types';
import { useMemo } from 'preact/hooks';
import { useBasicAutosaver } from '@components/autosaver';
import { showError } from '@components/app-error';
import { isNetworkError } from 'client/lib/ajax';
import { substateUpdater } from 'client/lib/hooks/substate-updater';
import { EmptyPage } from './empty-page';

const emptySelection: SalesPageState['selection'] = { id: '', value: { start: [] } };
const emptyContent: SalespageContent = { blockIds: [], blocks: {} };
export const defaultTheme: PageTheme = {
  fonts: [],
  appliedFonts: {},
  cta: { bgcolor: '#1e293b', fgcolor: '#ffffff' },
};

type CalendarAccountInfo = {
  isPublished: boolean;
  customDomain?: string;
  hostId: string;
};

type PageBuilderState = {
  content: SalespageContent;
  hasExistingContent: boolean;
  theme: PageTheme;
  selectedId: string;
  selection: typeof emptySelection;
  viewMode: 'desktop' | 'mobile';
  accountInfo: CalendarAccountInfo;
};

function createInitialState(
  overrides?: Partial<PageBuilderState>,
): Omit<PageBuilderState, 'accountInfo'> {
  return {
    content: emptyContent,
    hasExistingContent: false,
    theme: defaultTheme,
    selectedId: '',
    selection: emptySelection,
    viewMode: 'desktop' as const,
    ...overrides,
  };
}

export async function load({ auth }: RouteLoadProps): Promise<PageBuilderState> {
  const hostId = auth.user!.id;
  const result = await rpx.ruzcal.getPublicCalendarPage({ hostId });
  const content = result.content
    ? { blocks: result.content.blocks, blockIds: result.content.blockIds }
    : emptyContent;
  return {
    ...createInitialState({
      content,
      hasExistingContent: !!result.content?.blockIds?.length,
      theme: result.content?.theme || defaultTheme,
      selectedId: result.content?.blockIds?.[0] || '',
    }),
    accountInfo: { customDomain: result.customDomain, isPublished: result.isPublished, hostId },
  };
}

export type Props = LoadedProps<typeof load> & {
  publicURL: string;
};

export function CalendarPageBuilder(props: Props) {
  const { content, hasExistingContent, theme, selectedId, selection, viewMode } = props.state;
  const { setState } = props;

  // Create a combined state for autosaving that includes both content and theme
  const contentWithTheme = useMemo(() => ({ content, theme }), [content, theme]);

  const autosaver = useBasicAutosaver(contentWithTheme, async (val) => {
    try {
      return await rpx.ruzcal.saveCalendarPageContent({
        content: val.content,
        theme: val.theme,
      });
    } catch (err) {
      if (!isNetworkError(err)) {
        showError(err);
      }
    }
  });

  // Helper to create a setState function that works with SalesPageState shape
  const salesPageStateUpdater = useMemo(
    () =>
      substateUpdater(
        setState,
        (state): SalesPageState => ({
          selectedId: state.selectedId,
          selection: state.selection,
          viewMode: state.viewMode,
          content: state.content,
        }),
        (state, salesPageState) => ({ ...state, ...salesPageState }),
      ),
    [setState, selectedId, selection, viewMode, content],
  );

  const handleContentGenerated = (newContent: SalespageContent) => {
    setState((s) => ({
      ...s,
      ...createInitialState({
        content: newContent,
        hasExistingContent: true,
        theme,
        selectedId: newContent.blockIds[0] || '',
      }),
    }));
  };

  const resetPage = () => {
    setState((prev) => ({
      ...prev,
      ...createInitialState({ theme: prev.theme }),
    }));
  };

  if (!hasExistingContent) {
    return <EmptyPage onContentGenerated={handleContentGenerated} />;
  }

  const currentState: SalesPageState = {
    selectedId,
    selection,
    viewMode,
    content,
  };

  return (
    <CalendarPageEditor
      title="Calendar Landing Page"
      state={currentState}
      setState={salesPageStateUpdater}
      publicURL={props.publicURL}
      isConnected={autosaver.isConnected}
      isDirty={autosaver.isDirty}
      blockDefinitions={blockDefinitions}
      onReset={resetPage}
      theme={theme}
      setTheme={(theme) => setState((prev) => ({ ...prev, theme }))}
    />
  );
}
